import React from 'react'

import {
  Badge,
  Box,
  Icon,
  Tag,
  Text,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { isMaterializedProcessCompleted } from 'modules/processes/lib/materializedProcess'

export enum DateStatuses {
  Today = 'today',
  Tomorrow = 'tomorrow',
  Overdue = 'overview',
  Future = 'future',
  NoDueDate = 'no_due_date',
}

type TagProps = {
  date: Moment | null
}

export function DueDate({ date }: TagProps) {
  const valid = date?.isValid()

  return (
    <TooltipContent label={valid && date ? dateToString(date, 'ui') : null}>
      <TagLabel date={date} />
    </TooltipContent>
  )
}

const TooltipContent = ({
  label,
  children,
}: {
  label?: string | null
  children: React.ReactNode
}) => {
  const [open, toggle] = useToggle(false)

  return (
    <Tooltip.Alt
      open={label ? open : false}
      toggle={toggle}
      attachment="top center"
      label={label}
      textAlign="center"
    >
      {children}
    </Tooltip.Alt>
  )
}

function TagLabel({ date }: TagProps) {
  const MAP_STATUS_COLOUR = {
    [DateStatuses.Today]: Tag.Types.champagne,
    [DateStatuses.Tomorrow]: Tag.Types.champagne,
    [DateStatuses.Overdue]: Tag.Types.radical,
    [DateStatuses.Future]: Tag.Types.viridian,
    [DateStatuses.NoDueDate]: Tag.Types.grey,
  } as const

  if (date === null || !date?.isValid()) {
    return (
      <Tag ellipsis={false} type={MAP_STATUS_COLOUR[DateStatuses.NoDueDate]}>
        {i18n.t('processes.process.employee.table.columns.next_task_due.no_due_date')}
      </Tag>
    )
  }

  const today = moment()
  const tomorrow = moment().add(1, 'days')

  if (date.isSame(today, 'day')) {
    return (
      <Tag type={MAP_STATUS_COLOUR[DateStatuses.Today]}>
        {i18n.t('processes.process.employee.table.columns.next_task_due.today')}
      </Tag>
    )
  }
  if (date.isSame(tomorrow, 'day')) {
    return (
      <Tag type={MAP_STATUS_COLOUR[DateStatuses.Tomorrow]}>
        {i18n.t('processes.process.employee.table.columns.next_task_due.tomorrow')}
      </Tag>
    )
  }
  if (date.isBefore()) {
    return (
      <Tag ellipsis={false} type={MAP_STATUS_COLOUR[DateStatuses.Overdue]}>
        {date.fromNow()}
      </Tag>
    )
  }

  return <Tag type={MAP_STATUS_COLOUR[DateStatuses.Future]}>{date.fromNow()}</Tag>
}

function dateStatus({ date }: TagProps) {
  const today = moment()
  const tomorrow = moment().add(1, 'days')
  if (!date || !date?.isValid()) {
    return DateStatuses.NoDueDate
  }

  if (date.isSame(today, 'day')) {
    return DateStatuses.Today
  }
  if (date.isSame(tomorrow, 'day')) {
    return DateStatuses.Tomorrow
  }
  if (date.isBefore()) {
    return DateStatuses.Overdue
  }

  return DateStatuses.Future
}

type Props = {
  nextDueDate?: string | null
  status: string
}

export const NextTaskDueDate = ({ nextDueDate, status }: Props) => {
  const MAP_STATUS_COLOUR_FOR_ICON = {
    [DateStatuses.Today]: 'champagne1000',
    [DateStatuses.Tomorrow]: 'champagne1000',
    [DateStatuses.Overdue]: 'radicalRed1000',
    [DateStatuses.Future]: 'viridian1000',
    [DateStatuses.NoDueDate]: 'grey1000',
  } as const

  const MAP_STATUS_COLOUR_FOR_BADGE = {
    [DateStatuses.Today]: 'champagne200',
    [DateStatuses.Tomorrow]: 'champagne200',
    [DateStatuses.Overdue]: 'radicalRed200',
    [DateStatuses.Future]: 'viridian200',
    [DateStatuses.NoDueDate]: 'grey200',
  } as const

  const toolTipData = () => {
    const dateEarliest = nextDueDate ? stringToDate(nextDueDate, 'backend_datetime') : null
    const status = nextDueDate ? dateStatus({ date: dateEarliest }) : DateStatuses.NoDueDate
    const color = MAP_STATUS_COLOUR_FOR_ICON[status]
    const bagdeColor = MAP_STATUS_COLOUR_FOR_BADGE[status]
    switch (status) {
      case DateStatuses.Today:
        return {
          color,
          bagdeColor,
          label: i18n.t('processes.process.employee.table.columns.next_task_due.tasks_due_today'),
        }
      case DateStatuses.Tomorrow:
        return {
          color,
          bagdeColor,
          label: i18n.t(
            'processes.process.employee.table.columns.next_task_due.tasks_due_tomorrow'
          ),
        }
      case DateStatuses.Overdue:
        return {
          color,
          bagdeColor,
          label: i18n.t('processes.process.employee.table.columns.next_task_due.tasks_overdue'),
        }
      case DateStatuses.Future:
        return {
          color,
          bagdeColor,
          label: i18n.t('processes.process.employee.table.columns.next_task_due.tasks_on_due'),
        }
      case DateStatuses.NoDueDate:
        return {
          color,
          bagdeColor,
          label: i18n.t('processes.process.employee.table.columns.next_task_due.no_due_date'),
        }
    }
  }

  const { label, color, bagdeColor } = toolTipData()

  if (isMaterializedProcessCompleted(status))
    return (
      <Box flexDirection="row" width="full" justifyContent="center">
        <Badge color="grey200" size="s24">
          <Text.Normal lineClamp={2} color="grey600">
            -
          </Text.Normal>
        </Badge>
      </Box>
    )

  return (
    <Box flexDirection="row" width="full" justifyContent="center">
      <TooltipContent label={label}>
        <Badge color={bagdeColor} size="s24">
          <Icon.Medium icon="calendar" color={color} />
        </Badge>
      </TooltipContent>
    </Box>
  )
}
